import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { useContext } from 'react';
import { DropdownIndicatorProps } from 'react-select';
import styled, { keyframes, ThemeContext } from 'styled-components';

import { GraphDataContext } from 'dashboard/components/GraphModule/GraphDataWrapper';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { AccountStatus } from 'dashboard/models/account-status';
import { Graph } from 'dashboard/models/graph';
import { GraphQuery, GraphQueryStatus } from 'dashboard/models/graph-query';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import QueryDefinition from 'dashboard/modules/GraphV2/QueryDefinition';
import { StateAndContext } from 'dashboard/modules/GraphV2/types';

const StyledReactSelect = styled(ReactSelect<string>)`
  margin-top: auto;
  min-width: 200px !important;
  padding-left: ${Spacing[4]};
`;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const CheckmarkLoading = styled('svg')`
  border-radius: 50%;
  display: block;
  animation: ${rotate} linear 0.9s;
  animation-iteration-count: infinite;
`;

const CircleLoading = styled('circle')`
  stroke-dasharray: 20 30;
  stroke-dashoffset: 10;
  stroke-linecap: round;
  stroke: ${Color.CorePurple500};
`;

const GraphQueryTemplateOptionLabelContainer = styled('div')<{
  hasFailed: boolean;
}>`
  display: flex;
  color: ${({ theme, hasFailed }) => (hasFailed ? Color.CoreRed900 : theme.color.primary.black)};
`;

const QueryDefinitionContainer = styled.div`
  margin-right: ${Spacing[1]};
`;

const GraphQueryTemplateOptionText = styled('span')`
  padding-top: ${Spacing[1]};
  text-wrap: wrap;
  width: 120px;
`;

// negative margins is ugly, but the ReactSelect is adding too much padding and
// this is the simplist fix
const GraphQueryOptionGroupLabel = styled('div')`
  margin-left: -${Spacing[2]};
`;

const LoadingContainer = styled.div`
  width: 30px;
`;

const DropdownIndicatorContainer = styled.div<{
  disabled: boolean;
}>`
  display: inline-flex;
  color: ${({ theme, disabled }) =>
    disabled ? theme.color.text.disabled : theme.color.primary.black};
  pointer-events: none;
  margin-top: 6px;
`;

interface GraphQueryTemplateOptionLabelProps {
  graph: Graph;
  graphQueryTemplate: GraphQueryTemplate;
  isLoading: boolean;
  hasFailed: boolean;
}

function GraphQueryTemplateOptionLabel({
  graph,
  graphQueryTemplate,
  isLoading,
  hasFailed,
}: GraphQueryTemplateOptionLabelProps) {
  const { accountStatuses } = useContext(GraphDataContext);

  const name = graphQueryTemplate.attributes?.name || 'Unknown';

  return (
    <GraphQueryTemplateOptionLabelContainer hasFailed={hasFailed}>
      <GraphQueryTemplateOptionText>{name}</GraphQueryTemplateOptionText>
      <QueryDefinitionContainer>
        <QueryDefinition
          graph={graph}
          accountStatuses={accountStatuses as AccountStatus[]}
          graphQueryOrTemplate={graphQueryTemplate}
        />
      </QueryDefinitionContainer>
      {isLoading && (
        <LoadingContainer>
          <CheckmarkLoading width={18} height={18}>
            <CircleLoading cx='9' cy='9' r='8' fill='none' />
          </CheckmarkLoading>
        </LoadingContainer>
      )}
    </GraphQueryTemplateOptionLabelContainer>
  );
}

interface AttachedGraphQueryOptionLabelProps {
  graph: Graph;
  graphQuery: GraphQuery;
  graphQueryTemplate: GraphQueryTemplate;
  isLoading: boolean;
}

function AttachedGraphQueryOptionLabel({
  graph,
  graphQueryTemplate,
  graphQuery,
  isLoading,
}: AttachedGraphQueryOptionLabelProps) {
  const { accountStatuses } = useContext(GraphDataContext);

  const name = graphQueryTemplate.attributes?.name || 'Graph Query';

  const hasFailed = [GraphQueryStatus.Errored, GraphQueryStatus.TimedOut].includes(
    graphQuery.attributes.status,
  );
  return (
    <GraphQueryTemplateOptionLabelContainer hasFailed={hasFailed}>
      <GraphQueryTemplateOptionText>{name}</GraphQueryTemplateOptionText>
      <QueryDefinitionContainer>
        <QueryDefinition
          graph={graph}
          accountStatuses={accountStatuses as AccountStatus[]}
          graphQueryOrTemplate={graphQuery}
        />
      </QueryDefinitionContainer>
      {isLoading && (
        <LoadingContainer>
          <CheckmarkLoading width={18} height={18}>
            <CircleLoading cx='9' cy='9' r='8' fill='none' />
          </CheckmarkLoading>
        </LoadingContainer>
      )}
    </GraphQueryTemplateOptionLabelContainer>
  );
}

interface GraphQueryOptionSelectorProps {
  stateAndContext: StateAndContext;
  switchGraphQuery: (optionId: string) => void;
}

export const GraphQueryOptionSelector = ({
  stateAndContext,
  switchGraphQuery,
}: GraphQueryOptionSelectorProps) => {
  const {
    graphQueryTemplates,
    validAttachedGraphQueries,
    gqtsToGenerateGraphQueriesFor,
    graphQueryOptionIdToGraphQuery,
    graphQueryToGraphQueryInfo,
    selectedGraphQueryOptionId,
    graph,
  } = stateAndContext;

  const attachedGraphQueryOptions = validAttachedGraphQueries
    ?.map((graphQuery: GraphQuery) => {
      const graphQueryTemplate = graphQueryTemplates.find((gqt) => {
        return graphQuery.relationships['graph-query-template']?.data?.id === gqt.id;
      });
      const graphQueryInfo = graphQueryToGraphQueryInfo.current[graphQuery?.id];

      const isLoading = graphQueryInfo == null;
      // as we checked for the gqt earlier, graphQueryTemplate should most definitely exist
      return (
        graphQueryTemplate && {
          label: (
            <AttachedGraphQueryOptionLabel
              graph={graph}
              graphQueryTemplate={graphQueryTemplate}
              graphQuery={graphQuery}
              isLoading={isLoading}
            />
          ),
          value: graphQuery.id,
          disabled: isLoading,
        }
      );
    })
    .filter(Boolean);
  const attachedGraphQueryOptionGroup = attachedGraphQueryOptions?.length > 0 && {
    label: <GraphQueryOptionGroupLabel>Attached Graph Queries</GraphQueryOptionGroupLabel>,
    options: attachedGraphQueryOptions,
  };

  const graphQueryTemplateOptions = gqtsToGenerateGraphQueriesFor.map(
    (graphQueryTemplate: GraphQueryTemplate) => {
      const baseQuery = graphQueryOptionIdToGraphQuery.current[graphQueryTemplate.id];
      const graphQueryInfo = graphQueryToGraphQueryInfo.current[baseQuery?.id];

      const isLoading = graphQueryInfo == null;
      const hasFailed = [GraphQueryStatus.Errored, GraphQueryStatus.TimedOut].includes(
        graphQueryInfo?.graphQuery?.attributes?.status,
      );
      return {
        label: (
          <GraphQueryTemplateOptionLabel
            isLoading={isLoading}
            hasFailed={hasFailed}
            graph={graph}
            graphQueryTemplate={graphQueryTemplate}
          />
        ),
        value: graphQueryTemplate.id,
        disabled: isLoading,
      };
    },
  );
  const graphQueryTemplateOptionGroup = graphQueryTemplateOptions.length > 0 && {
    label: <GraphQueryOptionGroupLabel>Graph Query Templates</GraphQueryOptionGroupLabel>,
    options: graphQueryTemplateOptions,
  };

  const allOptionGroups = [attachedGraphQueryOptionGroup, graphQueryTemplateOptionGroup].filter(
    Boolean,
  );

  return (
    allOptionGroups.length > 0 && (
      <StyledReactSelect
        components={{
          DropdownIndicator: (props: DropdownIndicatorProps<OptionType>) => {
            const { isMulti, value, isDisabled } = props.selectProps;
            const theme = useContext(ThemeContext);

            // We only hide the dropdown indicator for multiselect. Always show for single.
            const hidden = isMulti && Array.isArray(value) && value.length > 0;

            // overwrite React-Select props.theme
            return hidden ? null : (
              <DropdownIndicatorContainer disabled={isDisabled} theme={theme}>
                <CaretDownIcon />
              </DropdownIndicatorContainer>
            );
          },
        }}
        classNamePrefix='GraphEditorDropdown'
        options={allOptionGroups}
        size='sm'
        isSearchable={false}
        value={selectedGraphQueryOptionId}
        onChange={(optionId: string) => {
          switchGraphQuery(optionId);
        }}
        portal={true}
      />
    )
  );
};
